import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { apiRequest, formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { motion } from 'framer-motion';
import { 
  Users, 
  FileText, 
  AlertCircle, 
  DollarSign, 
  TrendingUp,
  BarChart3,
  PieChart,
  Clock,
  CheckCircle2,
  Settings,
  MessageSquare,
  HelpCircle,
  Edit,
  Download,
  Mail
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Cell, Pie } from 'recharts';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [claims, setClaims] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analyticsData, usersData, claimsData, quotesData, inquiriesData] = await Promise.all([
          apiRequest('/admin/analytics'),
          apiRequest('/admin/users'),
          apiRequest('/claims'),
          apiRequest('/quotes'),
          apiRequest('/admin/inquiries').catch(() => [])
        ]);
        setAnalytics(analyticsData);
        setUsers(usersData);
        setClaims(claimsData);
        setQuotes(quotesData || []);
        setInquiries(inquiriesData || []);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
        toast.error('Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await apiRequest(`/admin/users/${userId}/role?role=${newRole}`, {
        method: 'PATCH'
      });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('User role updated');
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleClaimStatusChange = async (claimId, newStatus) => {
    try {
      await apiRequest(`/claims/${claimId}/status?status=${newStatus}`, {
        method: 'PATCH'
      });
      setClaims(claims.map(c => c.id === claimId ? { ...c, status: newStatus } : c));
      toast.success('Claim status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  const stats = [
    { label: 'Total Users', value: analytics?.total_users || 0, icon: Users, color: 'bg-blue-100 text-blue-600' },
    { label: 'Active Policies', value: analytics?.active_policies || 0, icon: FileText, color: 'bg-emerald-100 text-emerald-600' },
    { label: 'Pending Claims', value: analytics?.pending_claims || 0, icon: Clock, color: 'bg-amber-100 text-amber-600' },
    { label: 'Total Revenue', value: formatCurrency(analytics?.total_revenue || 0), icon: DollarSign, color: 'bg-purple-100 text-purple-600' }
  ];

  const policyChartData = Object.entries(analytics?.policies_by_type || {}).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));

  const claimsChartData = Object.entries(analytics?.claims_by_status || {}).map(([name, value]) => ({
    name: name.replace('_', ' '),
    value
  }));

  const COLORS = ['#0F172A', '#3B82F6', '#10B981', '#F59E0B', '#64748B'];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
          <p className="text-slate-600 mt-1">Manage users, policies, and claims</p>
        </div>

        {/* Quick Actions - Admin Tools */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Link to="/admin/content" className="block">
            <Card className="border-slate-200 hover:border-[#1a365d] transition-colors cursor-pointer h-full">
              <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                <Edit className="h-8 w-8 text-[#1a365d] mb-2" />
                <p className="font-medium text-slate-900">Edit Content</p>
                <p className="text-xs text-slate-500">Manage website text</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/inquiries" className="block">
            <Card className="border-slate-200 hover:border-[#1a365d] transition-colors cursor-pointer h-full">
              <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                <Mail className="h-8 w-8 text-[#1a365d] mb-2" />
                <p className="font-medium text-slate-900">Inquiries</p>
                <p className="text-xs text-slate-500">{inquiries.filter(i => i.status === 'new').length} new</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/settings" className="block">
            <Card className="border-slate-200 hover:border-[#1a365d] transition-colors cursor-pointer h-full">
              <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                <Settings className="h-8 w-8 text-[#1a365d] mb-2" />
                <p className="font-medium text-slate-900">Settings</p>
                <p className="text-xs text-slate-500">Site configuration</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/help" className="block">
            <Card className="border-slate-200 hover:border-[#1a365d] transition-colors cursor-pointer h-full">
              <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                <HelpCircle className="h-8 w-8 text-[#1a365d] mb-2" />
                <p className="font-medium text-slate-900">Help</p>
                <p className="text-xs text-slate-500">Troubleshooting</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="border-slate-200" data-testid={`admin-stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">{stat.label}</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{stat.value}</p>
                    </div>
                    <div className={`h-10 w-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="h-5 w-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Policies by Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={policyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <PieChart className="h-5 w-5" />
                Claims by Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                    <Pie
                      data={claimsChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {claimsChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RePieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Users, Claims, and Quotes */}
        <Tabs defaultValue="users" className="space-y-4">
          <TabsList>
            <TabsTrigger value="users" data-testid="tab-users">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="claims" data-testid="tab-claims">Claims ({claims.length})</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes">Quotes ({quotes.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} data-testid={`user-row-${u.id}`}>
                          <td className="font-medium">{u.first_name} {u.last_name}</td>
                          <td className="text-slate-600">{u.email}</td>
                          <td>
                            <span className={`badge ${u.role === 'admin' ? 'badge-info' : u.role === 'client' ? 'badge-success' : 'badge-neutral'}`}>
                              {u.role}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${u.is_active ? 'badge-success' : 'badge-danger'}`}>
                              {u.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <Select
                              value={u.role}
                              onValueChange={(value) => handleRoleChange(u.id, value)}
                            >
                              <SelectTrigger className="w-32 h-8" data-testid={`role-select-${u.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="prospect">Prospect</SelectItem>
                                <SelectItem value="client">Client</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="claims">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Claim #</th>
                        <th>Description</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {claims.map((claim) => (
                        <tr key={claim.id} data-testid={`claim-row-${claim.id}`}>
                          <td className="font-medium">{claim.claim_number}</td>
                          <td className="text-slate-600 max-w-xs truncate">{claim.description}</td>
                          <td>{formatDate(claim.incident_date)}</td>
                          <td>
                            <span className={`badge ${getStatusColor(claim.status)}`}>
                              {claim.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td>
                            <Select
                              value={claim.status}
                              onValueChange={(value) => handleClaimStatusChange(claim.id, value)}
                            >
                              <SelectTrigger className="w-36 h-8" data-testid={`claim-status-select-${claim.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="under_review">Under Review</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="denied">Denied</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quotes">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-lg">Quote Submissions</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    try {
                      const data = await apiRequest('/admin/export/quotes');
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `quotes-export-${new Date().toISOString().split('T')[0]}.json`;
                      a.click();
                      toast.success('Quotes exported!');
                    } catch (error) {
                      toast.error('Failed to export');
                    }
                  }}
                >
                  <Download className="h-4 w-4 mr-2" /> Export
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Quote #</th>
                        <th>Type</th>
                        <th>Contact</th>
                        <th>Premium</th>
                        <th>Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotes.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center text-slate-500 py-8">No quotes yet</td>
                        </tr>
                      ) : (
                        quotes.map((quote) => (
                          <tr key={quote.id} data-testid={`quote-row-${quote.id}`}>
                            <td className="font-medium">{quote.quote_number}</td>
                            <td className="capitalize">{quote.quote_type}</td>
                            <td className="text-slate-600">
                              {quote.request_data?.email || quote.request_data?.first_name || 'N/A'}
                            </td>
                            <td className="font-medium text-green-600">{formatCurrency(quote.estimated_premium)}</td>
                            <td>{formatDate(quote.created_at)}</td>
                            <td>
                              <span className={`badge ${quote.status === 'generated' ? 'badge-success' : quote.status === 'accepted' ? 'badge-info' : 'badge-neutral'}`}>
                                {quote.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
